import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAudience } from '../context/AudienceContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  MessageCircle,
  Send,
  Copy,
  Bot,
  User,
  Loader2,
  AlertTriangle,
  Trash2,
  Sparkles
} from 'lucide-react';

import { API_URL } from '../config';

const suggestedQuestions = [
  "Douleur thoracique aiguë: conduite à tenir?",
  "Posologie adrénaline anaphylaxie adulte?",
  "Protocole EME pédiatrique?",
  "Interactions amiodarone + AVK?",
  "Critères de thrombolyse AVC?"
];

export default function ChatPage() {
  const { token } = useAuth();
  const { audience, isAdult } = useAudience();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle initial message from AIInstantButton
  useEffect(() => {
    if (location.state?.initialMessage) {
      const initialMsg = location.state.initialMessage;
      sendMessage(initialMsg);
      // Clear the state to prevent re-sending on page refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const sendMessage = async (messageText = input) => {
    if (!messageText.trim()) return;

    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          messages: [...messages, userMessage],
          audience,
          context: { mode: isAdult ? 'adulte' : 'pédiatrique' }
        })
      });

      if (response.ok) {
        const data = await response.json();
        const assistantMessage = {
          role: 'assistant',
          content: data.response,
          timestamp: data.timestamp
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        toast.error('Erreur lors de l\'envoi du message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const copyMessage = (content) => {
    navigator.clipboard.writeText(content);
    toast.success('Message copié');
  };

  const clearChat = () => {
    setMessages([]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="h-[calc(100vh-56px)] flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-[hsl(var(--border))] bg-[hsl(var(--bg-elev))]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-cyan-400/20 flex items-center justify-center glow-cyan">
              <Bot className="w-5 h-5 text-cyan-300" />
            </div>
            <div>
              <h1 className="font-semibold">Assistant UrgenceMed</h1>
              <p className="text-xs text-[hsl(var(--text-dim))]">Mode {isAdult ? 'adulte' : 'pédiatrique'}</p>
            </div>
          </div>
          {messages.length > 0 && (
            <Button variant="ghost" size="sm" onClick={clearChat}>
              <Trash2 className="w-4 h-4 mr-1" />
              Effacer
            </Button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <div className="w-20 h-20 rounded-2xl bg-cyan-400/10 flex items-center justify-center mb-4 glow-cyan">
              <Sparkles className="w-10 h-10 text-cyan-300" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Comment puis-je vous aider?</h2>
            <p className="text-[hsl(var(--text-dim))] mb-6 max-w-md">
              Posez-moi vos questions sur les urgences médicales, posologies, protocoles ou interactions.
            </p>
            
            {/* Suggested Questions */}
            <div className="space-y-2 w-full max-w-md">
              <p className="text-xs text-[hsl(var(--muted))] mb-2">Suggestions:</p>
              {suggestedQuestions.map((question, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(question)}
                  className="w-full text-left p-3 rounded-lg bg-[hsl(var(--bg-elev))] border border-[hsl(var(--border))] hover:border-cyan-400/40 transition-colors text-sm"
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((msg, index) => (
            <div
              key={index}
              className={`chat-message ${msg.role}`}
              data-testid="chat-message"
            >
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${msg.role === 'user' ? 'bg-cyan-400/20' : 'bg-[hsl(var(--bg))]'}`}>
                  {msg.role === 'user' ? (
                    <User className="w-4 h-4 text-cyan-300" />
                  ) : (
                    <Bot className="w-4 h-4 text-cyan-300" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-[hsl(var(--text-dim))]">
                      {msg.role === 'user' ? 'Vous' : 'UrgenceMed'}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyMessage(msg.content)}
                      className="opacity-0 group-hover:opacity-100 -mr-2"
                      data-testid="chat-copy-button"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                  <div className="prose prose-invert prose-sm max-w-none">
                    <pre className="whitespace-pre-wrap text-sm font-sans">{msg.content}</pre>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
        
        {loading && (
          <div className="chat-message assistant">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[hsl(var(--bg))] flex items-center justify-center">
                <Loader2 className="w-4 h-4 text-cyan-300 animate-spin" />
              </div>
              <div className="flex gap-1">
                <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Disclaimer */}
      <div className="px-4 py-2 bg-amber-500/10 border-t border-amber-500/30">
        <div className="flex items-center gap-2 text-xs text-amber-400">
          <AlertTriangle className="w-3 h-3 flex-shrink-0" />
          <span>Aide à la décision clinique uniquement. Validez toute prescription avec un médecin.</span>
        </div>
      </div>

      {/* Input */}
      <div className="p-4 pb-16 border-t border-[hsl(var(--border))] bg-[hsl(var(--bg-elev))] relative" style={{ zIndex: 9999 }}>
        <div className="flex gap-2 max-w-4xl mx-auto">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Posez votre question médicale..."
            className="flex-1 bg-transparent"
            disabled={loading}
            data-testid="chat-input"
          />
          <Button
            onClick={() => sendMessage()}
            disabled={loading || !input.trim()}
            className="bg-cyan-400/90 text-black hover:bg-cyan-300"
            style={{ position: 'relative', zIndex: 10000 }}
            data-testid="chat-send-button"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
